import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatTZS, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  BedDouble, UtensilsCrossed, Waves, Plane, MapPin, Phone, Mail, MessageCircle,
  ArrowRight, Users, CalendarDays, Wifi, ShowerHead, Coffee, Star, Clock, Compass, Building2, Check,
} from "lucide-react";

const iconFor = (title) => {
  const t = (title || "").toLowerCase();
  if (t.includes("din") || t.includes("kitchen")) return <UtensilsCrossed className="w-6 h-6" />;
  if (t.includes("safari") || t.includes("trek")) return <Waves className="w-6 h-6" />;
  if (t.includes("airport") || t.includes("transfer")) return <Plane className="w-6 h-6" />;
  return <BedDouble className="w-6 h-6" />;
};

const amenityIcon = (a) => {
  const t = a.toLowerCase();
  if (t.includes("wifi")) return <Wifi className="w-3.5 h-3.5" />;
  if (t.includes("shower")) return <ShowerHead className="w-3.5 h-3.5" />;
  if (t.includes("break")) return <Coffee className="w-3.5 h-3.5" />;
  return null;
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

export default function Landing() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [types, setTypes] = useState([]);
  const [checkIn, setCheckIn] = useState(todayStr());
  const [checkOut, setCheckOut] = useState(addDays(1));
  const [adults, setAdults] = useState("2");
  const [children, setChildren] = useState("0");
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [activities, setActivities] = useState([]);
  const [services, setServices] = useState([]);
  const [svcBookOpen, setSvcBookOpen] = useState(false);
  const [selectedSvc, setSelectedSvc] = useState(null);
  const [svcForm, setSvcForm] = useState({ guest_name: "", guest_email: "", guest_phone: "", event_date: todayStr(), guests: 1, notes: "" });
  const [svcSubmitting, setSvcSubmitting] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteSvc, setQuoteSvc] = useState(null);
  const [quoteForm, setQuoteForm] = useState({ guest_name: "", guest_email: "", guest_phone: "", event_date: todayStr(), guests: 1, menu_notes: "", requirements: "" });
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const [s, t, r, a, sv] = await Promise.all([
        api.get("/settings"), api.get("/room-types"),
        api.get("/reviews"), api.get("/activities"),
        api.get("/services"),
      ]);
      setSettings(s.data);
      setTypes(t.data);
      setReviews(r.data);
      setActivities(a.data);
      setServices(sv.data);
    })();
  }, []);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!checkIn || !checkOut) return toast.error("Please select both check-in and check-out dates");
    if (new Date(checkOut) <= new Date(checkIn)) return toast.error("Check-out must be after check-in");
    setSearching(true);
    try {
      const { data } = await api.post("/room-types/search", {
        check_in_date: checkIn, check_out_date: checkOut,
        adults: Number(adults) || 1, children: Number(children) || 0,
      });
      setTypes(data.room_types || []);
      setSearched(true);
      setTimeout(() => document.getElementById("rooms")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
      if ((data.room_types || []).length === 0) {
        toast.info("No rooms match those dates/guests. Try adjusting your search.");
      }
    } catch (err) {
      toast.error(formatApiError(err) || "Search failed. Please try again.");
    } finally {
      setSearching(false);
    }
  };

  const bookType = (typeId) => {
    const params = new URLSearchParams({ check_in: checkIn, check_out: checkOut, adults, children });
    navigate(`/book/${typeId}?${params.toString()}`);
  };

  const openServiceBooking = (svc) => {
    setSelectedSvc(svc);
    setSvcForm({ guest_name: "", guest_email: "", guest_phone: "", event_date: todayStr(), guests: 1, notes: "" });
    setSvcBookOpen(true);
  };

  const submitServiceBooking = async (e) => {
    e.preventDefault();
    if (!svcForm.guest_name || !svcForm.guest_email || !svcForm.guest_phone) return toast.error("Please fill in your name, email and phone");
    setSvcSubmitting(true);
    try {
      const { data } = await api.post(`/services/${selectedSvc.id}/book`, { ...svcForm, guests: Number(svcForm.guests) || 1 });
      toast.success(`Request received! Reference ${data.reference}. Reception will confirm shortly.`);
      setSvcBookOpen(false);
    } catch (e) { toast.error(formatApiError(e) || "Booking failed"); }
    finally { setSvcSubmitting(false); }
  };

  const openQuoteFor = (svc) => {
    setQuoteSvc(svc || null);
    setQuoteForm({ guest_name: "", guest_email: "", guest_phone: "", event_date: todayStr(), guests: 1, menu_notes: "", requirements: "" });
    setQuoteOpen(true);
  };

  const submitQuote = async (e) => {
    e.preventDefault();
    if (!quoteForm.guest_name || !quoteForm.guest_email || !quoteForm.guest_phone) return toast.error("Please fill in your name, email and phone");
    setQuoteSubmitting(true);
    try {
      const { data } = await api.post(`/service-quotes`, {
        ...quoteForm,
        guests: Number(quoteForm.guests) || 1,
        service_id: quoteSvc?.id || null,
        service_title: quoteSvc?.title || "Custom package",
      });
      toast.success(`Quote request ${data.reference} sent — we'll reply soon`);
      setQuoteOpen(false);
      // Redirect to quote status page so guest can track it
      setTimeout(() => navigate(`/quote/${data.reference}`), 800);
    } catch (e) { toast.error(formatApiError(e) || "Failed to send request"); }
    finally { setQuoteSubmitting(false); }
  };

  if (!settings) return <div className="min-h-screen flex items-center justify-center"><p className="font-ui text-sm text-[color:var(--muted-text)]">Loading Baraka Inn…</p></div>;

  return (
    <div className="min-h-screen bg-[color:var(--sand)]" data-testid="landing-page">
      <nav className="absolute top-0 inset-x-0 z-30 px-6 sm:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-[color:var(--brand)] flex items-center justify-center text-white font-heading font-bold">B</div>
          <span className="font-heading text-xl sm:text-2xl text-white drop-shadow">Baraka Inn</span>
        </div>
        <div className="hidden md:flex items-center gap-8 text-white/95 font-ui text-sm">
          <a href="#rooms" className="hover:text-[color:var(--gold)] transition-colors">Rooms</a>
          <a href="#services" className="hover:text-[color:var(--gold)] transition-colors">Services</a>
          <a href="#gallery" className="hover:text-[color:var(--gold)] transition-colors">Gallery</a>
          <a href="#contact" className="hover:text-[color:var(--gold)] transition-colors">Contact</a>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/lookup" data-testid="nav-lookup-booking" className="font-ui text-xs sm:text-sm px-4 py-2 rounded-full bg-white/15 backdrop-blur border border-white/30 text-white hover:bg-white/25 transition-colors">Find booking</Link>
          <Link to="/admin/login" data-testid="nav-admin-login" className="font-ui text-xs sm:text-sm px-4 py-2 rounded-full bg-white/15 backdrop-blur border border-white/30 text-white hover:bg-white/25 transition-colors">Staff</Link>
        </div>
      </nav>

      <section className="relative min-h-[100svh] flex items-end overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${settings.hero_image})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/70" />
        <div className="relative z-10 w-full px-6 sm:px-10 pb-16 sm:pb-24 max-w-7xl mx-auto text-white fade-up">
          <p className="font-ui uppercase tracking-[0.3em] text-xs sm:text-sm text-[color:var(--gold)] mb-4">{settings.tagline}</p>
          <h1 className="font-heading text-4xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-3xl">
            {settings.hotel_name.split(" ").slice(0, -1).join(" ")} <em className="text-[color:var(--gold)] not-italic">{settings.hotel_name.split(" ").slice(-1)}</em>
          </h1>
          <p className="mt-6 max-w-xl font-body text-base sm:text-lg text-white/90">{settings.description}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#search" data-testid="hero-book-cta" className="px-6 py-3 rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)] text-white font-ui text-sm font-medium transition-colors flex items-center gap-2">Book Your Stay <ArrowRight className="w-4 h-4" /></a>
            <a href="#rooms" className="px-6 py-3 rounded-full bg-white/10 backdrop-blur border border-white/40 hover:bg-white/20 text-white font-ui text-sm font-medium transition-colors">Explore Rooms</a>
          </div>
        </div>
      </section>

      <section id="search" className="relative -mt-16 z-20 px-4 sm:px-10 max-w-6xl mx-auto">
        <form onSubmit={handleSearch} className="bg-white shadow-2xl rounded-3xl p-6 sm:p-8 border border-[color:var(--brand)]/10" data-testid="availability-search-form">
          <h2 className="font-heading text-2xl sm:text-3xl text-[color:var(--deep)] mb-6">Check availability</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div><Label className="font-ui text-xs uppercase tracking-wider text-[color:var(--muted-text)]">Check-in</Label><Input type="date" value={checkIn} min={todayStr()} onChange={(e) => setCheckIn(e.target.value)} data-testid="search-checkin" className="mt-1 font-ui" /></div>
            <div><Label className="font-ui text-xs uppercase tracking-wider text-[color:var(--muted-text)]">Check-out</Label><Input type="date" value={checkOut} min={checkIn} onChange={(e) => setCheckOut(e.target.value)} data-testid="search-checkout" className="mt-1 font-ui" /></div>
            <div><Label className="font-ui text-xs uppercase tracking-wider text-[color:var(--muted-text)]">Adults</Label>
              <Select value={adults} onValueChange={setAdults}><SelectTrigger data-testid="search-adults" className="mt-1 font-ui"><SelectValue /></SelectTrigger><SelectContent>{[1, 2, 3, 4, 5, 6].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label className="font-ui text-xs uppercase tracking-wider text-[color:var(--muted-text)]">Children</Label>
              <Select value={children} onValueChange={setChildren}><SelectTrigger data-testid="search-children" className="mt-1 font-ui"><SelectValue /></SelectTrigger><SelectContent>{[0, 1, 2, 3, 4].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="col-span-2 md:col-span-1 flex items-end">
              <Button type="submit" disabled={searching} data-testid="search-submit" className="w-full h-10 bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)] text-white font-ui rounded-lg">
                {searching ? "Searching…" : "Check Availability"}
              </Button>
            </div>
          </div>
        </form>
      </section>

      <section id="rooms" className="px-6 sm:px-10 py-20 sm:py-28 max-w-7xl mx-auto" data-testid="rooms-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="font-ui uppercase tracking-[0.25em] text-xs text-[color:var(--brand)]">{searched ? "Available for your dates" : "Our rooms"}</p>
            <h2 className="font-heading text-3xl sm:text-5xl text-[color:var(--deep)] mt-2">{searched ? `${types.length} room type${types.length === 1 ? "" : "s"} available` : "Comfort at a great value"}</h2>
          </div>
        </div>

        <div id="results" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 stagger">
          {types.map((r) => (
            <article key={r.id} className="bg-white rounded-3xl overflow-hidden shadow-sm border border-[color:var(--brand)]/10 hover-lift" data-testid={`room-type-card-${r.id}`}>
              <div className="h-64 arch-top relative">
                <img src={r.images?.[0] || "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?auto=format&fit=crop&w=1200&q=80"} alt={r.name} className="w-full h-full object-cover" />
                <div className="absolute top-4 right-4 px-3 py-1 rounded-full bg-white/95 backdrop-blur text-xs font-ui font-semibold text-[color:var(--deep)]">
                  {searched && r.available_units != null ? `${r.available_units} available` : `${r.total_units || 0} rooms`}
                </div>
              </div>
              <div className="p-6">
                <h3 className="font-heading text-2xl text-[color:var(--deep)]">{r.name}</h3>
                <p className="mt-2 font-body text-sm text-[color:var(--muted-text)] line-clamp-2">{r.description}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {(r.amenities || []).slice(0, 4).map((a) => (
                    <span key={a} className="text-xs font-ui text-[color:var(--deep)] px-2.5 py-1 rounded-full bg-[color:var(--sand)] border border-[color:var(--brand)]/15 flex items-center gap-1">{amenityIcon(a)}{a}</span>
                  ))}
                </div>
                <div className="mt-5 flex items-end justify-between">
                  <div>
                    {searched ? (
                      <>
                        <p className="font-ui text-xs uppercase tracking-wider text-[color:var(--muted-text)]">{r.nights ? `${r.nights} night${r.nights > 1 ? "s" : ""} total` : "Per night"}</p>
                        {r.discount?.applied ? (
                          <>
                            <div className="flex items-baseline gap-2">
                              <p className="font-heading text-2xl text-[color:var(--brand)]" data-testid={`price-${r.id}`}>{formatTZS(r.total_price)}</p>
                              <p className="font-ui text-sm text-[color:var(--muted-text)] line-through">{formatTZS(r.base_total)}</p>
                            </div>
                            <span className="mt-1 inline-block text-[10px] font-ui uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full" data-testid={`discount-badge-${r.id}`}>{r.discount.label} · −{r.discount.percent}%</span>
                          </>
                        ) : (
                          <p className="font-heading text-2xl text-[color:var(--brand)]" data-testid={`price-${r.id}`}>{formatTZS(r.total_price ?? r.price_per_night)}</p>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="font-ui text-xs uppercase tracking-wider text-[color:var(--muted-text)]">Great value</p>
                        <p className="font-ui text-sm text-[color:var(--deep)] font-medium">Check availability for rates</p>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs font-ui text-[color:var(--muted-text)]"><Users className="w-3.5 h-3.5" /> {r.capacity_adults}A · {r.capacity_children}C</div>
                </div>
                <Button onClick={() => bookType(r.id)} disabled={searched && r.available_units === 0} data-testid={`book-room-type-${r.id}`} className="w-full mt-5 bg-[color:var(--deep)] hover:bg-[color:var(--brand-dark)] text-white font-ui rounded-full">
                  {searched ? "Book this room" : "Check availability"} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </article>
          ))}
          {searched && types.length === 0 && (
            <div className="col-span-full text-center py-12 space-y-3">
              <p className="font-body text-[color:var(--muted-text)]">No rooms match your search. Our largest room fits 3 adults + 2 children — try fewer guests or different dates.</p>
              <Button onClick={() => { setSearched(false); setTypes([]); setAdults("2"); setChildren("0"); }} variant="outline" data-testid="reset-search-btn" className="font-ui">Reset search</Button>
            </div>
          )}
        </div>
      </section>

      <section id="services" className="px-6 sm:px-10 py-20 bg-[color:var(--deep)] text-white relative overflow-hidden grain">
        <div className="max-w-7xl mx-auto relative">
          <p className="font-ui uppercase tracking-[0.25em] text-xs text-[color:var(--gold)]">Experiences</p>
          <h2 className="font-heading text-3xl sm:text-5xl mt-2 max-w-xl">Services curated for the Northern circuit</h2>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger">
            {(settings.services || []).map((s, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur hover:bg-white/10 transition-colors overflow-hidden" data-testid={`service-${i}`}>
                {s.image ? (
                  <div className="w-full h-40 rounded-xl overflow-hidden mb-5 -mx-2 -mt-2">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-[color:var(--brand)] flex items-center justify-center text-white">{iconFor(s.title)}</div>
                )}
                <h3 className={`font-heading text-xl ${s.image ? "" : "mt-5"}`}>{s.title}</h3>
                <p className="mt-2 font-body text-sm text-white/70">{s.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="gallery" className="px-6 sm:px-10 py-20 max-w-7xl mx-auto">
        <p className="font-ui uppercase tracking-[0.25em] text-xs text-[color:var(--brand)]">Moments</p>
        <h2 className="font-heading text-3xl sm:text-5xl text-[color:var(--deep)] mt-2">Inside Baraka Inn</h2>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
          {(settings.gallery || []).map((src, i) => (
            <div key={i} className={`overflow-hidden rounded-2xl ${i === 0 ? "row-span-2 md:h-[520px]" : "h-56 md:h-64"}`}>
              <img src={src} alt="Baraka Inn" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
          ))}
        </div>
      </section>

      {activities.length > 0 && (
        <section id="activities" className="px-6 sm:px-10 py-20 max-w-7xl mx-auto" data-testid="activities-section">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <p className="font-ui uppercase tracking-[0.25em] text-xs text-[color:var(--brand)]">Adventures nearby</p>
              <h2 className="font-heading text-3xl sm:text-5xl text-[color:var(--deep)] mt-2">Serengeti · Ngorongoro · Kilimanjaro</h2>
              <p className="mt-3 font-body text-[color:var(--muted-text)] max-w-2xl">Trips we book on your behalf. Add them at checkout or ask reception to tailor a package.</p>
            </div>
            <span className="text-[11px] font-ui text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5">💡 Final prices reconfirmed at reception</span>
          </div>          <div className="flex items-end justify-between gap-6 flex-wrap">
            <div>
              <p className="font-ui uppercase tracking-[0.25em] text-xs text-[color:var(--brand)] flex items-center gap-2"><Compass className="w-3.5 h-3.5" /> Activities &amp; tours</p>
              <h2 className="font-heading text-3xl sm:text-5xl text-[color:var(--deep)] mt-2">Curated adventures</h2>
              <p className="mt-3 font-body text-[color:var(--muted-text)] max-w-md">Ask reception to arrange any of these while you stay — everything organised in advance.</p>
            </div>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
            {activities.map((a) => (
              <article key={a.id} className="bg-white rounded-3xl overflow-hidden shadow-sm border border-[color:var(--brand)]/10 hover-lift" data-testid={`activity-${a.id}`}>
                <div className="h-56 overflow-hidden">
                  {a.image ? <img src={a.image} alt={a.title} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[color:var(--sand)] flex items-center justify-center"><Compass className="w-10 h-10 text-[color:var(--brand)]/40" /></div>}
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-2xl text-[color:var(--deep)]">{a.title}</h3>
                  <p className="mt-2 font-body text-sm text-[color:var(--muted-text)]">{a.description}</p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="font-heading text-lg text-[color:var(--brand)]">{a.price > 0 ? formatTZS(a.price) : "Ask reception"}</span>
                    {a.duration && <span className="text-xs font-ui text-[color:var(--muted-text)] flex items-center gap-1"><Clock className="w-3 h-3" /> {a.duration}</span>}
                  </div>
                  {(a.includes || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {a.includes.map((t, i) => (
                        <span key={i} className="text-[11px] font-ui text-[color:var(--deep)] bg-[color:var(--sand)] border border-[color:var(--brand)]/15 px-2 py-0.5 rounded-full">{t}</span>
                      ))}
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {services.length > 0 && (
        <section id="facilities" className="px-6 sm:px-10 py-20 bg-white border-y border-[color:var(--brand)]/10" data-testid="facilities-section">
          <div className="max-w-7xl mx-auto">
            <p className="font-ui uppercase tracking-[0.25em] text-xs text-[color:var(--brand)] flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> Facilities &amp; packages</p>
            <h2 className="font-heading text-3xl sm:text-5xl text-[color:var(--deep)] mt-2">Book meeting halls, private dinners &amp; transfers</h2>
            <p className="mt-3 font-body text-[color:var(--muted-text)] max-w-2xl">All packages come with the essentials — drinks, refreshments, meals — clearly listed below. Book online or tap "Ask reception" for custom quotes.</p>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
              {services.map((s) => (
                <article key={s.id} className="bg-white rounded-3xl overflow-hidden shadow-sm border border-[color:var(--brand)]/10 hover-lift flex flex-col" data-testid={`facility-${s.id}`}>
                  <div className="h-52 overflow-hidden relative">
                    {s.image ? <img src={s.image} alt={s.title} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[color:var(--sand)] flex items-center justify-center"><Building2 className="w-10 h-10 text-[color:var(--brand)]/40" /></div>}
                    {s.price_at_reception && <span className="absolute top-3 left-3 text-[11px] font-ui px-2 py-1 rounded-full bg-amber-500/95 text-white">Ask reception</span>}
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="font-heading text-2xl text-[color:var(--deep)]">{s.title}</h3>
                    <p className="mt-2 font-body text-sm text-[color:var(--muted-text)] line-clamp-2">{s.description}</p>
                    <div className="mt-3 flex items-center justify-between text-sm">
                      <span className="font-heading text-lg text-[color:var(--brand)]" data-testid={`facility-price-${s.id}`}>{s.price_at_reception ? "Price at reception" : (s.price > 0 ? formatTZS(s.price) : "Free")}</span>
                      {s.duration && <span className="text-xs font-ui text-[color:var(--muted-text)] flex items-center gap-1"><Clock className="w-3 h-3" /> {s.duration}</span>}
                    </div>
                    {s.capacity && <p className="mt-1 text-xs font-ui text-[color:var(--muted-text)] flex items-center gap-1"><Users className="w-3 h-3" /> {s.capacity}</p>}
                    {(s.includes || []).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {s.includes.map((t, i) => (
                          <span key={i} className="text-[11px] font-ui text-[color:var(--deep)] bg-[color:var(--sand)] border border-[color:var(--brand)]/15 px-2 py-0.5 rounded-full flex items-center gap-1"><Check className="w-2.5 h-2.5 text-[color:var(--brand)]" /> {t}</span>
                        ))}
                      </div>
                    )}
                    <Button onClick={() => openServiceBooking(s)} data-testid={`book-facility-${s.id}`} className="mt-5 w-full bg-[color:var(--deep)] hover:bg-[color:var(--brand-dark)] text-white font-ui rounded-full">
                      Book this service <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                    <button type="button" onClick={() => openQuoteFor(s)} data-testid={`quote-facility-${s.id}`} className="mt-2 w-full text-xs font-ui text-[color:var(--brand)] hover:underline">
                      Or request a custom quote →
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}


      {reviews.length > 0 && (
        <section id="reviews" className="px-6 sm:px-10 py-20 bg-[color:var(--sand)] border-y border-[color:var(--brand)]/10" data-testid="reviews-section">
          <div className="max-w-7xl mx-auto">
            <p className="font-ui uppercase tracking-[0.25em] text-xs text-[color:var(--brand)] flex items-center gap-2"><Star className="w-3.5 h-3.5" /> What guests say</p>
            <h2 className="font-heading text-3xl sm:text-5xl text-[color:var(--deep)] mt-2">Stories from our stayers</h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
              {reviews.slice(0, 6).map((r) => (
                <div key={r.id} className="bg-white rounded-2xl p-6 shadow-sm border border-[color:var(--brand)]/10 hover-lift" data-testid={`review-${r.id}`}>
                  <div className="flex">{[...Array(5)].map((_, i) => (<Star key={i} className={`w-4 h-4 ${i < r.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />))}</div>
                  {r.title && <p className="mt-3 font-heading text-lg text-[color:var(--deep)]">&ldquo;{r.title}&rdquo;</p>}
                  <p className={`${r.title ? "mt-2" : "mt-3"} font-body text-sm text-[color:var(--muted-text)]`}>{r.comment}</p>
                  <p className="mt-4 text-xs font-ui uppercase tracking-wider text-[color:var(--brand)]">— {r.guest_name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section id="contact" className="px-6 sm:px-10 py-20 bg-white border-t border-[color:var(--brand)]/10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <p className="font-ui uppercase tracking-[0.25em] text-xs text-[color:var(--brand)]">Get in touch</p>
            <h2 className="font-heading text-3xl sm:text-5xl text-[color:var(--deep)] mt-2">Visit Baraka Inn</h2>
            <p className="mt-4 font-body text-[color:var(--muted-text)] max-w-md">Reach us any time — reception is happy to help with reservations, tours, or special requests.</p>
            <div className="mt-8 space-y-4 font-ui text-sm">
              <a href={settings.map_url || `https://maps.google.com/?q=${settings.latitude || ""},${settings.longitude || ""}`} target="_blank" rel="noreferrer" className="flex items-start gap-3 text-[color:var(--deep)] hover:text-[color:var(--brand)] transition-colors">
                <MapPin className="w-5 h-5 mt-0.5 shrink-0 text-[color:var(--brand)]" /><span data-testid="contact-address">{settings.address}</span>
              </a>
              <a href={`tel:${settings.phone}`} className="flex items-center gap-3 text-[color:var(--deep)] hover:text-[color:var(--brand)] transition-colors"><Phone className="w-5 h-5 text-[color:var(--brand)]" /><span data-testid="contact-phone">{settings.phone}</span></a>
              <a href={`mailto:${settings.email}`} className="flex items-center gap-3 text-[color:var(--deep)] hover:text-[color:var(--brand)] transition-colors"><Mail className="w-5 h-5 text-[color:var(--brand)]" /><span data-testid="contact-email">{settings.email}</span></a>
              {settings.whatsapp && <a href={`https://wa.me/${settings.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-[color:var(--deep)] hover:text-[color:var(--brand)] transition-colors"><MessageCircle className="w-5 h-5 text-[color:var(--brand)]" /><span data-testid="contact-whatsapp">WhatsApp {settings.whatsapp}</span></a>}
            </div>
          </div>
          <div className="h-96 rounded-3xl overflow-hidden bg-[color:var(--sand)]">
            <iframe title="Baraka Inn location" className="w-full h-full border-0" src={settings.latitude && settings.longitude ? `https://maps.google.com/maps?q=${settings.latitude},${settings.longitude}&t=&z=16&ie=UTF8&iwloc=&output=embed` : `https://maps.google.com/maps?q=${encodeURIComponent(settings.address)}&t=&z=13&ie=UTF8&iwloc=&output=embed`} />
          </div>
        </div>
      </section>

      {/* Custom quote request dialog */}
      <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
        <DialogContent data-testid="quote-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request custom quote {quoteSvc ? `— ${quoteSvc.title}` : ""}</DialogTitle>
            <DialogDescription>Tell us your numbers and menu preferences — reception will reply with a live price you can accept online.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitQuote} className="space-y-3">
            <div><Label>Full name</Label><Input required value={quoteForm.guest_name} onChange={(e) => setQuoteForm({ ...quoteForm, guest_name: e.target.value })} data-testid="quote-name" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input required type="email" value={quoteForm.guest_email} onChange={(e) => setQuoteForm({ ...quoteForm, guest_email: e.target.value })} data-testid="quote-email" className="mt-1" /></div>
              <div><Label>Phone</Label><Input required value={quoteForm.guest_phone} onChange={(e) => setQuoteForm({ ...quoteForm, guest_phone: e.target.value })} data-testid="quote-phone" className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Event date</Label><Input type="date" required min={todayStr()} value={quoteForm.event_date} onChange={(e) => setQuoteForm({ ...quoteForm, event_date: e.target.value })} data-testid="quote-date" className="mt-1" /></div>
              <div><Label>Number of guests</Label><Input type="number" min="1" value={quoteForm.guests} onChange={(e) => setQuoteForm({ ...quoteForm, guests: e.target.value })} data-testid="quote-guests" className="mt-1" /></div>
            </div>
            <div><Label>Menu / catering preferences</Label><Textarea rows={3} value={quoteForm.menu_notes} onChange={(e) => setQuoteForm({ ...quoteForm, menu_notes: e.target.value })} placeholder="e.g. Vegetarian buffet lunch, 2 coffee breaks with pastries…" data-testid="quote-menu" className="mt-1" /></div>
            <div><Label>Other requirements</Label><Textarea rows={2} value={quoteForm.requirements} onChange={(e) => setQuoteForm({ ...quoteForm, requirements: e.target.value })} placeholder="Setup style, projector, WiFi, decor…" data-testid="quote-req" className="mt-1" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setQuoteOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={quoteSubmitting} data-testid="quote-submit" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]">
                {quoteSubmitting ? "Sending…" : "Request quote"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Service booking dialog */}
      <Dialog open={svcBookOpen} onOpenChange={setSvcBookOpen}>
        <DialogContent data-testid="service-booking-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Book {selectedSvc?.title}</DialogTitle>
            <DialogDescription>
              {selectedSvc?.price_at_reception ? "Price will be confirmed at reception." : (selectedSvc?.price > 0 ? `Rate: ${formatTZS(selectedSvc?.price || 0)}` : "Free")}
              {(selectedSvc?.includes || []).length > 0 && (
                <span className="mt-2 flex flex-wrap gap-1">
                  {selectedSvc.includes.map((t, i) => (<span key={i} className="text-[11px] text-[color:var(--deep)] bg-[color:var(--sand)] border border-[color:var(--brand)]/15 px-2 py-0.5 rounded-full">✓ {t}</span>))}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitServiceBooking} className="space-y-3">
            <div><Label>Full name</Label><Input required value={svcForm.guest_name} onChange={(e) => setSvcForm({ ...svcForm, guest_name: e.target.value })} data-testid="svc-book-name" className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input required type="email" value={svcForm.guest_email} onChange={(e) => setSvcForm({ ...svcForm, guest_email: e.target.value })} data-testid="svc-book-email" className="mt-1" /></div>
              <div><Label>Phone</Label><Input required value={svcForm.guest_phone} onChange={(e) => setSvcForm({ ...svcForm, guest_phone: e.target.value })} data-testid="svc-book-phone" className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Event date</Label><Input type="date" required min={todayStr()} value={svcForm.event_date} onChange={(e) => setSvcForm({ ...svcForm, event_date: e.target.value })} data-testid="svc-book-date" className="mt-1" /></div>
              <div><Label>Guests</Label><Input type="number" min="1" value={svcForm.guests} onChange={(e) => setSvcForm({ ...svcForm, guests: e.target.value })} data-testid="svc-book-guests" className="mt-1" /></div>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={svcForm.notes} onChange={(e) => setSvcForm({ ...svcForm, notes: e.target.value })} placeholder="Menu preferences, setup style, dietary needs…" data-testid="svc-book-notes" className="mt-1" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSvcBookOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={svcSubmitting} data-testid="svc-book-submit" className="bg-[color:var(--brand)] hover:bg-[color:var(--brand-dark)]">
                {svcSubmitting ? "Sending…" : "Send booking request"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <footer className="bg-[color:var(--deep)] text-white/70 py-10 px-6 sm:px-10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 font-ui text-sm">
          <p>© {new Date().getFullYear()} {settings.hotel_name}. All rights reserved.</p>
          <div className="flex items-center gap-4">
            {settings.social_media && Object.entries(settings.social_media).filter(([, v]) => v?.enabled && v?.url).map(([platform, v]) => {
              const icons = {
                instagram: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z",
                facebook: "M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z",
                twitter: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z",
                tiktok: "M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005.8 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1.84-.1z",
              };
              const colors = { instagram: "hover:text-pink-400", facebook: "hover:text-blue-400", twitter: "hover:text-white", tiktok: "hover:text-emerald-400" };
              return (
                <a key={platform} href={v.url} target="_blank" rel="noreferrer" data-testid={`social-link-${platform}`} aria-label={platform} title={platform}
                   className={`text-white/60 ${colors[platform] || ""} transition-colors`}>
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true"><path d={icons[platform]} /></svg>
                </a>
              );
            })}
            <div className="flex items-center gap-2 text-[color:var(--gold)]"><CalendarDays className="w-4 h-4" /><span>Karibu — Welcome home</span></div>
          </div>
        </div>
      </footer>

      {settings.whatsapp && (
        <a
          href={`https://wa.me/${settings.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(`Habari! I'd like to enquire about ${settings.hotel_name}.`)}`}
          target="_blank"
          rel="noreferrer"
          data-testid="floating-whatsapp"
          className="fixed bottom-6 right-6 z-40 group flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-[#25D366] text-white shadow-2xl hover:shadow-emerald-500/50 hover:scale-105 transition-all"
          aria-label="Chat on WhatsApp"
        >
          <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.296-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          <span className="hidden sm:inline font-ui text-sm font-medium">Chat on WhatsApp</span>
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full animate-pulse" />
        </a>
      )}
    </div>
  );
}
